import { useMemo } from "react";
import { useConfig } from "@/contexts/ConfigContext";
import {
  CheckCircle2, Circle, ChevronRight, Sparkles, TrendingUp, Rocket,
} from "lucide-react";

/**
 * Checklist de completitud del perfil profesional.
 * Muestra progreso visual y links directos a cada sección faltante.
 * Sólo aplicable a lawyer / mediator.
 */
export default function ProfileCompletionCard({ user }) {
  const { config } = useConfig();
  const checklist = useMemo(() => buildChecklist(user, config), [user, config]);
  const total = checklist.length;
  const done = checklist.filter((c) => c.done).length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const complete = pct === 100;

  const scrollTo = (testid) => {
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className={`panel-glow p-6 mt-6 relative overflow-hidden ${
        complete ? "border-emerald-400/40" : "border-amber-400/30"
      }`}
      data-testid="profile-completion-card"
    >
      {/* Halo decorativo */}
      <div
        className={`pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl ${
          complete ? "bg-emerald-500/20" : "bg-amber-500/10"
        }`}
      />

      <div className="flex items-start justify-between gap-4 flex-wrap relative z-10">
        <div>
          <div className="label-eyebrow inline-flex items-center gap-1.5">
            {complete ? (
              <><Sparkles size={12} className="text-emerald-400" /> Perfil completo</>
            ) : (
              <><Rocket size={12} className="text-amber-400" /> Completa tu perfil</>
            )}
          </div>
          <div className="serif text-2xl mt-1">
            {complete
              ? "🎉 ¡Perfil 100% completado!"
              : `${done} de ${total} pasos completados`}
          </div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-xl">
            {complete
              ? "Estás listo/a para recibir casos, aparecer en el directorio público y recibir pagos vía Pago Protegido."
              : "Cada campo suma para aparecer en el directorio público, poder postular a casos y recibir pagos. Completa lo que falta para desbloquear todas las funciones."}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="relative w-16 h-16">
            <svg viewBox="0 0 36 36" className="w-16 h-16 -rotate-90">
              <circle
                cx="18" cy="18" r="15.9155"
                fill="none"
                stroke="currentColor"
                className="text-white/10"
                strokeWidth="2.8"
              />
              <circle
                cx="18" cy="18" r="15.9155"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.8"
                strokeLinecap="round"
                strokeDasharray={`${pct}, 100`}
                className={complete ? "text-emerald-400" : "text-amber-400"}
              />
            </svg>
            <div
              className={`absolute inset-0 flex items-center justify-center text-lg font-bold ${
                complete ? "text-emerald-300" : "text-amber-300"
              }`}
              data-testid="profile-completion-percent"
            >
              {pct}%
            </div>
          </div>
        </div>
      </div>

      {/* Barra de progreso lineal */}
      <div className="mt-5 h-1.5 rounded-full bg-white/10 overflow-hidden relative z-10">
        <div
          className={`h-full transition-all duration-700 ease-out ${
            complete ? "bg-emerald-400" : "bg-gradient-to-r from-amber-500 to-orange-400"
          }`}
          style={{ width: `${pct}%` }}
          data-testid="profile-completion-bar"
        />
      </div>

      {/* Lista */}
      {!complete && (
        <ul className="mt-5 space-y-1.5 relative z-10">
          {checklist.map((c) => (
            <li
              key={c.key}
              className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 transition ${
                c.done
                  ? "bg-emerald-500/5 text-emerald-200"
                  : "bg-white/[0.02] hover:bg-white/[0.05] cursor-pointer"
              }`}
              onClick={c.done ? undefined : () => scrollTo(c.scrollTo)}
              data-testid={`profile-check-${c.key}`}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                {c.done ? (
                  <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
                ) : (
                  <Circle size={16} className="text-muted-foreground shrink-0" />
                )}
                <div className="min-w-0">
                  <div className={`text-sm ${c.done ? "line-through opacity-60" : ""}`}>
                    {c.label}
                  </div>
                  {!c.done && c.hint && (
                    <div className="text-[11px] text-muted-foreground mt-0.5">{c.hint}</div>
                  )}
                </div>
              </div>
              {!c.done && (
                <div className="inline-flex items-center gap-1 text-xs text-amber-300 shrink-0">
                  Completar <ChevronRight size={12} />
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Recompensa cuando 100% */}
      {complete && (
        <div className="mt-5 grid sm:grid-cols-3 gap-3 relative z-10">
          <RewardBadge icon={<TrendingUp size={14}/>} title="Visible en directorio" desc="Los solicitantes ya te pueden encontrar" />
          <RewardBadge icon={<Rocket size={14}/>} title="Postulaciones activas" desc="Puedes bidear en delegaciones" />
          <RewardBadge icon={<Sparkles size={14}/>} title="Pago Protegido listo" desc="Puedes recibir transferencias" />
        </div>
      )}
    </div>
  );
}

function RewardBadge({ icon, title, desc }) {
  return (
    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3">
      <div className="inline-flex items-center gap-1.5 text-emerald-300 text-xs font-semibold">
        {icon} {title}
      </div>
      <div className="text-[11px] text-muted-foreground mt-1">{desc}</div>
    </div>
  );
}

/**
 * Construye la lista de tareas según rol y datos actuales del usuario.
 * Aplica a lawyer, mediator y requester.
 */
function buildChecklist(user, config) {
  if (!user) return [];
  const role = user.role;
  const list = [];

  const s = (v) => !!(v && String(v).trim());
  const isRequester = role === "requester";
  const isPro = role === "lawyer" || role === "mediator";

  // Foto de perfil: OPCIONAL para abogados (no cuenta al %). Sí requerida para
  // mediadores (aparecen en directorio público de mediación familiar). El
  // solicitante no aparece en ningún directorio y nunca la necesita.
  if (role === "mediator") {
    list.push({
      key: "avatar",
      label: "Foto de perfil",
      hint: "Genera más confianza en la plataforma",
      done: !!user.has_avatar,
      scrollTo: "avatar-uploader",
    });
  }
  list.push({
    key: "name",
    label: "Nombre completo",
    done: s(user.name),
    scrollTo: "prof-name",
  });
  list.push({
    key: "phone",
    label: "Teléfono de contacto",
    hint: "Formato +56 9 · 8 dígitos",
    done: s(user.phone),
    scrollTo: "prof-phone",
  });
  list.push({
    key: "rut",
    label: "RUT",
    done: s(user.rut),
    scrollTo: "prof-rut",
  });
  list.push({
    key: "region",
    label: "Región",
    done: s(user.region),
    scrollTo: "prof-region",
  });
  list.push({
    key: "comuna",
    label: "Comuna",
    done: s(user.comuna),
    scrollTo: "prof-comuna",
  });
  list.push({
    key: "domicilio",
    label: isRequester ? "Dirección Particular" : "Domicilio profesional",
    done: s(user.domicilio_profesional),
    scrollTo: "prof-address",
  });

  // Sólo profesionales necesitan estos campos.
  // Nota: N° Registro (Colegio de Abogados / Ministerio de Justicia) es OPCIONAL
  // para abogados y no cuenta para el % de progreso. Sí lo pedimos a mediadores.
  if (isPro) {
    if (role === "mediator") {
      list.push({
        key: "registry",
        label: "N° Registro Ministerio de Justicia",
        done: s(user.registry_number),
        scrollTo: "prof-registry",
      });
    }
    list.push({
      key: "bio",
      label: "Biografía profesional",
      hint: "Describe tu experiencia (mínimo 1 párrafo)",
      done: s(user.bio),
      scrollTo: "prof-bio",
    });
    // Banking (Pago Protegido) — sólo profesionales reciben pagos
    list.push({
      key: "banking",
      label: "Datos bancarios (Pago Protegido)",
      hint: "Necesario para recibir transferencias",
      done: !!user.banking_info_complete,
      scrollTo: "banking-info-section",
    });
  }

  // Mediador — extras del directorio
  if (role === "mediator") {
    const tariff = user.mediator_tariff || {};
    const base = tariff.valor_base_frustrada_clp || 0;
    const acuerdo = tariff.valor_acuerdo_total_clp || 0;
    const tariffOk = base > 0 && acuerdo >= base;
    // Chequeo de tope legal (misma fórmula que el backend) — lee la Configuración de Negocio dinámica
    const bc = config?.business_config || {};
    const INITIAL_FEE = Number(bc.cargo_fijo_mediacion ?? config?.colega_link_initial_fee_clp ?? 13200);
    const RATE = Number(bc.porcentaje_comision_acuerdo ?? config?.colega_link_agreement_rate ?? 0.08);
    const CAP = Number(bc.tope_total_solicitante_clp ?? config?.family_mediation_total_cap_clp ?? 130602);
    const adicional = Math.max(0, acuerdo - base);
    const totalSolicitante = base + INITIAL_FEE + adicional + Math.ceil(adicional * RATE);
    const withinCap = tariffOk && totalSolicitante <= CAP;

    list.push({
      key: "tariff",
      label: "Tarifario de mediación (base y acuerdo)",
      hint: "Valor de sesión base frustrada y valor con acuerdo",
      done: tariffOk,
      scrollTo: "mediator-office-form",
    });
    list.push({
      key: "tariffcap",
      label: "Tarifa dentro del tope legal",
      hint: `El total al solicitante no puede superar $${CAP.toLocaleString("es-CL")}`,
      done: withinCap,
      scrollTo: "mediator-office-form",
    });
    list.push({
      key: "cortes",
      label: "Cortes de Apelaciones habilitadas",
      done: (user.cortes_apelaciones || []).length > 0,
      scrollTo: "mediator-office-form",
    });
    list.push({
      key: "comunas",
      label: "Comunas donde atiendes",
      done: (user.comunas_atendidas || []).length > 0,
      scrollTo: "mediator-office-form",
    });
    list.push({
      key: "modalidad",
      label: "Modalidad de atención",
      hint: "Al menos presencial o telemática",
      done: !!(user.attends_presencial || user.attends_remote),
      scrollTo: "mediator-office-form",
    });
    list.push({
      key: "availability",
      label: "Horarios y disponibilidad semanal",
      hint: "Describe tu horario en el campo de texto del perfil",
      done: !!(user.horario_disponibilidad && String(user.horario_disponibilidad).trim().length > 0),
      scrollTo: "mediator-office-form",
    });
  }

  // Abogado — certificado (Colegio de Abogados)
  if (role === "lawyer") {
    list.push({
      key: "certificate",
      label: "Certificado de título subido",
      hint: "Requerido para postular a delegaciones",
      done: !!user.has_certificate,
      scrollTo: "certificate-card",
    });
  }

  return list;
}
